import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../../../api/agent";

export interface DealerSettings {
  id: string;
  dmsDealerId: number;
  hasMultipleSite: boolean;
  targetPaySystemId?: string;
  targetPayAPIKey?: string;
}

export interface TargetPayConfigRequest {
  dealerSettingId: string;
  systemId: string;
  apiKey: string;
}

export const dealerSettingsApi = createApi({
  reducerPath: "dealerSettingsApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["DealerSettings"],

  endpoints: (build) => ({
    getAllDealerSettings: build.mutation<any, void>({
      query: () => ({
        url: `api/DealerSettings/GetAll`,
        method: "get",
      }),
    }),
    sendDealerEmail: build.mutation<
      any,
      {
        FromName: string;
        EmailTo: string;
        Subject: string;
        Body: string;
      }
    >({
      query: (payload) => ({
        url: `api/DealerSettings/SendEmail`,
        method: "post",
        data: payload,
      }),
    }),
    getSubscriptions: build.query<any, void>({
      query: () => ({
        url: `api/DealerSettings/GetSubscriptions`,
        method: "get",
      }),
    }),
    getFeatures: build.query<any, void>({
      query: () => ({
        url: `api/DealerSettings/GetProductFeatures`,
        method: "get",
      }),
    }),
    markFeatureAsAddon: build.mutation<any, { featureId: string | number }>({
      query: ({ featureId }) => ({
        url: `api/DealerSettings/MarkFeatureAsAddon`,
        method: "post",
        data: { featureId },
      }),
    }),
    getAddOns: build.query<any, void>({
      query: () => ({
        url: `api/DealerSettings/AddOns`,
        method: "get",
      }),
    }),
    removeFeatureAsAddon: build.mutation<any, { featureId: string | number }>({
      query: ({ featureId }) => ({
        url: `api/DealerSettings/UnmarkFeatureAsAddon?featureId=${featureId}`,
        method: "delete",
      }),
    }),
    getAvailableFeaturesForDealer: build.query<any, { dealerGuid: string }>({
      query: ({ dealerGuid }) => ({
        url: `api/DealerSettings/GetAvailableFeaturesForDealer?dealerGuid=${dealerGuid}`,
        method: "get",
      }),
    }),
    assignAddOnToDealer: build.mutation<
      any,
      { dealerId: string; featureId: string }
    >({
      query: ({ dealerId, featureId }) => ({
        url: `api/DealerSettings/AssignAddOnToDealer`,
        method: "post",
        data: { dealerId, featureId },
      }),
    }),
    unAssignAddOnToDealer: build.mutation<
      any,
      { dealerId: string; featureId: string }
    >({
      query: ({ dealerId, featureId }) => ({
        url: `api/DealerSettings/UnAssignAddOnToDealer`,
        method: "post",
        data: { dealerId, featureId },
      }),
    }),
    updateTargetPayConfig: build.mutation<any, TargetPayConfigRequest>({
      query: (payload) => ({
        url: `api/DealerSettings/UpdateTargetPayConfig`,
        method: "put",
        data: payload,
      }),
      invalidatesTags: ["DealerSettings"],
    }),
  }),
});

export const {
  useGetAllDealerSettingsMutation,
  useSendDealerEmailMutation,
  useGetSubscriptionsQuery,
  useLazyGetSubscriptionsQuery,
  useLazyGetFeaturesQuery,
  useMarkFeatureAsAddonMutation,
  useLazyGetAddOnsQuery,
  useRemoveFeatureAsAddonMutation,
  useLazyGetAvailableFeaturesForDealerQuery,
  useAssignAddOnToDealerMutation,
  useUnAssignAddOnToDealerMutation,
  useUpdateTargetPayConfigMutation,
} = dealerSettingsApi;

export const dealerSettingsReducer = dealerSettingsApi.reducer;
